import FilterAltOffOutlinedIcon from "@mui/icons-material/FilterAltOffOutlined";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import SearchIcon from "@mui/icons-material/Search";
import {
  Alert,
  Box,
  Button,
  Grid,
  InputAdornment,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { frFR } from "@mui/x-data-grid/locales";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import SMQMainContainer from "../../../components/SMQMainContainer";
import useSmqPermissions from "../../../hooks/useSmqPermissions";
import ConstatService from "../../../services/smq/ConstatService";
import { getDisplayedNcStatus } from "../NC/ncStatusHelper";
import HeaderRegistre from "./HeaderRegistre";
import NCDetailDialog from "./NCDetailDialog";

const EMPTY_VALUE = "—";

const CRITICITE_COLORS = {
  critique: { backgroundColor: "#fde2e2", color: "#b42318" },
  majeure: { backgroundColor: "#fff3cd", color: "#9a5b00" },
  mineure: { backgroundColor: "#d8f8e6", color: "#107844" },
};

const STATUT_COLORS = {
  delai_depasse: { backgroundColor: "#ffe1e1", color: "#d32f2f" },
  in_progress: { backgroundColor: "#fff2bf", color: "#9a5b00" },
  open: { backgroundColor: "#e1efff", color: "#175b9c" },
  reopen: { backgroundColor: "#fde2e2", color: "#b42318" },
};

const normalize = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("fr");

const displayValue = (value) => String(value ?? "").trim() || EMPTY_VALUE;

const formatDate = (value) => {
  if (!value) return EMPTY_VALUE;
  const date = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("fr-FR");
};

const statusLabel = (value) => {
  if (value === "delai_depasse") return "Délai dépassé";
  if (value === "in_progress") return "En cours";
  if (value === "open") return "Ouverte";
  if (value === "reopen") return "NC réouverte — action insuffisante";
  return displayValue(value);
};

const filterFieldSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#fff",
    borderRadius: "6px",
    fontSize: 12,
    height: 40,
  },
  "& .MuiInputLabel-root": { fontSize: 12 },
};

const filterButtonSx = {
  borderColor: "#9fb1c3",
  borderRadius: "6px",
  color: "#173d61",
  fontSize: 11,
  fontWeight: 700,
  height: 38,
  minWidth: 160,
  textTransform: "none",
  width: { xs: "100%", sm: 160 },
  "&:hover": {
    backgroundColor: "#f3f7fa",
    borderColor: "#173d61",
  },
};

const AnalyseList = () => {
  const { canTreat } = useSmqPermissions();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [processusFilter, setProcessusFilter] = useState("");
  const [exigenceFilter, setExigenceFilter] = useState("");
  const [criticiteFilter, setCriticiteFilter] = useState("");
  const [statutFilter, setStatutFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [filtersVisible, setFiltersVisible] = useState(true);
  const [detailTarget, setDetailTarget] = useState(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const loadAnalyses = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const response = await ConstatService.getAnalysableConstats();
      if (!Array.isArray(response?.data)) {
        throw new Error("Le service d’analyse a retourné une réponse invalide.");
      }

      const analysableRows = new Map();
      response.data.forEach((item) => {
        const isParent = item.parent_id === null;
        const isOpenChild = item.parent_id !== null && item.statut === "open";

        if (isParent || isOpenChild) {
          analysableRows.set(String(item.constat_id), item);
        }
      });

      setRows(
        Array.from(analysableRows.values()).map((item) => ({
          ...item,
          id: item.constat_id,
          statut_affiche: getDisplayedNcStatus(item.statut, item.delai),
        }))
      );
    } catch (error) {
      setRows([]);
      setLoadError(error?.message || "Impossible de charger les NC à analyser.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAnalyses();
  }, [loadAnalyses]);

  useEffect(() => {
    setPaginationModel((current) => ({ ...current, page: 0 }));
  }, [
    criticiteFilter,
    dateFrom,
    dateTo,
    exigenceFilter,
    processusFilter,
    searchTerm,
    statutFilter,
  ]);

  const processusOptions = useMemo(
    () =>
      Array.from(new Set(rows.map((row) => row.processus).filter(Boolean))).sort(
        (left, right) => left.localeCompare(right, "fr")
      ),
    [rows]
  );

  const exigenceOptions = useMemo(
    () =>
      Array.from(new Set(rows.map((row) => row.exigence).filter(Boolean))).sort(
        (left, right) => left.localeCompare(right, "fr")
      ),
    [rows]
  );

  const filteredRows = useMemo(() => {
    const search = normalize(searchTerm);
    return rows.filter((row) => {
      const matchesSearch =
        !search ||
        normalize(
          `${row.reference} ${row.parent_reference} ${row.description_constat} ${row.processus}`
        ).includes(search);
      const matchesProcessus =
        !processusFilter || row.processus === processusFilter;
      const matchesExigence = !exigenceFilter || row.exigence === exigenceFilter;
      const matchesCriticite =
        !criticiteFilter || row.criticite === criticiteFilter;
      const matchesStatut =
        !statutFilter || row.statut_affiche === statutFilter;
      const constatDate = String(row.constat_date || "").slice(0, 10);
      const matchesDateFrom =
        !dateFrom || (constatDate && constatDate >= dateFrom);
      const matchesDateTo = !dateTo || (constatDate && constatDate <= dateTo);

      return (
        matchesSearch &&
        matchesProcessus &&
        matchesExigence &&
        matchesCriticite &&
        matchesStatut &&
        matchesDateFrom &&
        matchesDateTo
      );
    });
  }, [
    criticiteFilter,
    dateFrom,
    dateTo,
    exigenceFilter,
    processusFilter,
    rows,
    searchTerm,
    statutFilter,
  ]);

  const hasActiveFilters = Boolean(
    searchTerm ||
      processusFilter ||
      exigenceFilter ||
      criticiteFilter ||
      statutFilter ||
      dateFrom ||
      dateTo
  );

  const resetFilters = () => {
    setSearchTerm("");
    setProcessusFilter("");
    setExigenceFilter("");
    setCriticiteFilter("");
    setStatutFilter("");
    setDateFrom("");
    setDateTo("");
  };

  const openAnalyse = useCallback(
    (row) => {
      navigate(`/smq_nc/add?constat_id=${row.constat_id}&step=analyse`);
    },
    [navigate]
  );

  const columns = useMemo(
    () => [
      {
        field: "reference",
        flex: 1.1,
        headerName: "Code NC",
        minWidth: 190,
        renderCell: ({ row, value }) => (
          <Box sx={{ minWidth: 0, py: 0.5 }}>
            <Typography color="#10263d" fontSize={12} fontWeight={800} lineHeight={1.25} noWrap>
              {displayValue(value)}
            </Typography>
            {row.parent_id !== null && (
              <Typography color="#60758a" fontSize={10} lineHeight={1.25} noWrap>
                Parent : {row.parent_reference || "Parent non renseigné"}
              </Typography>
            )}
            {row.statut_affiche === "delai_depasse" && (
              <Typography color="#d32f2f" fontSize={10} fontWeight={700} lineHeight={1.25}>
                Délai dépassé
              </Typography>
            )}
          </Box>
        ),
      },
      {
        field: "constat_date",
        flex: 0.65,
        headerName: "Date",
        minWidth: 105,
        renderCell: ({ value }) => formatDate(value),
      },
      {
        field: "processus",
        flex: 0.9,
        headerName: "Processus",
        minWidth: 135,
        renderCell: ({ value }) => displayValue(value),
      },
      {
        field: "description_constat",
        flex: 1.6,
        headerName: "Constat",
        minWidth: 250,
        renderCell: ({ value }) => (
          <Typography fontSize={12} lineHeight={1.35}>
            {displayValue(value)}
          </Typography>
        ),
      },
      {
        field: "criticite",
        flex: 0.7,
        headerName: "Criticité",
        minWidth: 110,
        renderCell: ({ value }) => (
          <Box
            component="span"
            sx={{
              ...(CRITICITE_COLORS[value] || {
                backgroundColor: "#edf1f5",
                color: "#52677c",
              }),
              borderRadius: "999px",
              fontSize: 11,
              fontWeight: 800,
              px: 1.2,
              py: 0.5,
              textTransform: "capitalize",
            }}
          >
            {displayValue(value)}
          </Box>
        ),
      },
      {
        field: "statut_affiche",
        flex: 0.85,
        headerName: "Statut",
        minWidth: 130,
        renderCell: ({ value }) => (
          <Box
            component="span"
            sx={{
              ...(STATUT_COLORS[value] || {
                backgroundColor: "#edf1f5",
                color: "#52677c",
              }),
              borderRadius: "999px",
              fontSize: 11,
              fontWeight: 800,
              px: 1.2,
              py: 0.5,
            }}
          >
            {statusLabel(value)}
          </Box>
        ),
      },
      {
        align: "center",
        field: "action",
        filterable: false,
        headerAlign: "center",
        headerName: "Action",
        minWidth: 190,
        sortable: false,
        renderCell: ({ row }) => {
          if (!canTreat) return null;
          const hasAnalyse = Boolean(row.analyse_id);
          return (
            <Button
              aria-label={`${hasAnalyse ? "Compléter l’analyse" : "Analyser"} ${row.reference}`}
              onClick={() => openAnalyse(row)}
              size="small"
              startIcon={<ScienceOutlinedIcon />}
              sx={{
                backgroundColor: hasAnalyse ? "#fff7dc" : "#edfdf7",
                borderColor: hasAnalyse ? "#f3b21c" : "#56d6a3",
                borderRadius: "8px",
                color: hasAnalyse ? "#9a4d13" : "#08734c",
                fontSize: 12,
                fontWeight: 800,
                minWidth: hasAnalyse ? 168 : 118,
                textTransform: "none",
                "&:hover": {
                  backgroundColor: hasAnalyse ? "#ffefb7" : "#dcf8ee",
                  borderColor: hasAnalyse ? "#d99a09" : "#28b982",
                },
              }}
              variant="outlined"
            >
              {hasAnalyse ? "Compléter analyse" : "Analyser"}
            </Button>
          );
        },
      },
    ],
    [canTreat, openAnalyse]
  );

  return (
    <SMQMainContainer>
      <>
        <HeaderRegistre
          icon={<ScienceOutlinedIcon sx={{ color: "#56c596", fontSize: 16 }} />}
          title="Analyser les non-conformités"
        />
        <Box
          sx={{
            backgroundColor: "#f1f5fb",
            minHeight: "calc(100vh - 56px)",
            px: { xs: 1.5, sm: 2.5 },
            pb: 3,
          }}
        >
          {loadError && (
            <Alert
              action={
                <Button color="inherit" onClick={loadAnalyses} size="small">
                  Réessayer
                </Button>
              }
              severity="error"
              sx={{ mb: 1.5 }}
            >
              {loadError}
            </Alert>
          )}

          <Paper
            elevation={0}
            sx={{
              border: "1px solid #cbd8e6",
              borderRadius: "8px",
              p: 1.25,
            }}
          >
            <Box
              sx={{
                alignItems: "center",
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: 1,
                justifyContent: "space-between",
                mb: filtersVisible ? 1.5 : 0,
              }}
            >
              {filtersVisible && (
                <Typography color="#60758a" fontSize={11} whiteSpace="nowrap">
                  {filteredRows.length} entrées / {rows.length} total
                </Typography>
              )}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 1,
                  justifyContent: "flex-end",
                  ml: { sm: "auto" },
                  width: { xs: "100%", sm: "auto" },
                }}
              >
                {filtersVisible && (
                  <Button
                    color="inherit"
                    disabled={!hasActiveFilters}
                    onClick={resetFilters}
                    size="small"
                    startIcon={<RestartAltIcon />}
                    sx={filterButtonSx}
                    variant="outlined"
                  >
                    Réinitialiser
                  </Button>
                )}
                <Button
                  color="inherit"
                  onClick={() => setFiltersVisible((visible) => !visible)}
                  size="small"
                  startIcon={
                    filtersVisible ? (
                      <FilterAltOffOutlinedIcon />
                    ) : (
                      <FilterAltOutlinedIcon />
                    )
                  }
                  sx={filterButtonSx}
                  variant="outlined"
                >
                  {filtersVisible ? "Masquer filtres" : "Afficher filtres"}
                </Button>
              </Box>
            </Box>

            {filtersVisible && (
              <>
                <Typography
                  color="#52677c"
                  fontSize={10}
                  fontWeight={700}
                  letterSpacing="0.05em"
                  mb={1}
                >
                  FILTRES
                </Typography>
                <Grid alignItems="flex-start" container spacing={1.25}>
                  <Grid item md={6} sm={6} xs={12}>
                    <TextField
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon sx={{ color: "#62a5bd", fontSize: 18 }} />
                          </InputAdornment>
                        ),
                      }}
                      label="Recherche"
                      onChange={(event) => setSearchTerm(event.target.value)}
                      placeholder="Référence, constat ou processus..."
                      size="small"
                      sx={filterFieldSx}
                      value={searchTerm}
                    />
                  </Grid>
                  <Grid item md={3} sm={6} xs={12}>
                    <TextField
                      label="Processus"
                      onChange={(event) => setProcessusFilter(event.target.value)}
                      select
                      size="small"
                      sx={filterFieldSx}
                      value={processusFilter}
                    >
                      <MenuItem value="">Tous les processus</MenuItem>
                      {processusOptions.map((processus) => (
                        <MenuItem key={processus} value={processus}>
                          {processus}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item md={3} sm={6} xs={12}>
                    <TextField
                      label="Exigence"
                      onChange={(event) => setExigenceFilter(event.target.value)}
                      select
                      size="small"
                      sx={filterFieldSx}
                      value={exigenceFilter}
                    >
                      <MenuItem value="">Toutes les exigences</MenuItem>
                      {exigenceOptions.map((exigence) => (
                        <MenuItem key={exigence} value={exigence}>
                          {exigence}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item md={3} sm={6} xs={12}>
                    <TextField
                      label="Criticité"
                      onChange={(event) => setCriticiteFilter(event.target.value)}
                      select
                      size="small"
                      sx={filterFieldSx}
                      value={criticiteFilter}
                    >
                      <MenuItem value="">Toutes les criticités</MenuItem>
                      <MenuItem value="mineure">Mineure</MenuItem>
                      <MenuItem value="majeure">Majeure</MenuItem>
                      <MenuItem value="critique">Critique</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item md={3} sm={6} xs={12}>
                    <TextField
                      label="Statut"
                      onChange={(event) => setStatutFilter(event.target.value)}
                      select
                      size="small"
                      sx={filterFieldSx}
                      value={statutFilter}
                    >
                      <MenuItem value="">Tous les statuts</MenuItem>
                      <MenuItem value="open">Ouverte</MenuItem>
                      <MenuItem value="in_progress">En cours</MenuItem>
                      <MenuItem value="reopen">Réouverte</MenuItem>
                      <MenuItem value="delai_depasse">Délai dépassé</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item md={3} sm={6} xs={12}>
                    <TextField
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ max: dateTo || undefined }}
                      label="Du"
                      onChange={(event) => setDateFrom(event.target.value)}
                      size="small"
                      sx={filterFieldSx}
                      type="date"
                      value={dateFrom}
                    />
                  </Grid>
                  <Grid item md={3} sm={6} xs={12}>
                    <TextField
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ min: dateFrom || undefined }}
                      label="Au"
                      onChange={(event) => setDateTo(event.target.value)}
                      size="small"
                      sx={filterFieldSx}
                      type="date"
                      value={dateTo}
                    />
                  </Grid>
                </Grid>
              </>
            )}
          </Paper>

          <Paper
            elevation={0}
            sx={{
              border: "1px solid #cbd8e6",
              borderRadius: "8px",
              height: 560,
              mt: 2,
              overflow: "hidden",
            }}
          >
            <DataGrid
              columns={columns}
              disableRowSelectionOnClick
              loading={loading}
              localeText={frFR.components.MuiDataGrid.defaultProps.localeText}
              onPaginationModelChange={setPaginationModel}
              onRowDoubleClick={({ row }) => setDetailTarget(row)}
              pageSizeOptions={[10, 25, 50]}
              paginationModel={paginationModel}
              rowHeight={64}
              rows={filteredRows}
              sx={{
                border: 0,
                color: "#10263d",
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 12,
                "& .MuiDataGrid-cell": { borderColor: "#d5e0eb" },
                "& .MuiDataGrid-columnHeader": {
                  backgroundColor: "#173d61",
                  color: "#fff",
                  fontSize: 11,
                  fontWeight: 700,
                },
                "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus": {
                  outline: "2px solid #d5ad3f",
                  outlineOffset: "-2px",
                },
                "& .MuiDataGrid-columnSeparator, & .MuiDataGrid-sortIcon, & .MuiDataGrid-menuIconButton": {
                  color: "#fff",
                },
                "& .MuiDataGrid-row:hover": { backgroundColor: "#f6f9fc" },
                "& .MuiDataGrid-footerContainer": { borderColor: "#d5e0eb" },
              }}
            />
          </Paper>

          <NCDetailDialog
            onClose={() => setDetailTarget(null)}
            target={detailTarget}
          />
        </Box>
      </>
    </SMQMainContainer>
  );
};

export default AnalyseList;
